import { useEffect, useRef } from 'react';

import useLoadingStates from 'hooks/useLoadingStates';

import {
    useGetBookingChannelsQuery,
    useUpdateBookingChannelsMutation,
} from 'redux/slices/globalApi';

import { BOOKING_CHANNELS_LOADING_KEYS, toasterMessage } from '../constants';
import { STR_BOOKING_CHANNELS } from '../constants/bookingChannels';
import { getDisabledChannelValues } from '../utils';
import useIntegrationToaster from './useIntegrationToaster';

// Stable identity for the "nothing configured yet" case, so consumers are not
// handed a fresh array on every render.
const NO_DISABLED_CHANNELS = [];

/**
 * Read + save layer for the Manage Booking Channels drawer.
 *
 * Both directions hit `${setting}/${integrationId}/settings/values` and speak the
 * same shape — `{ settings: { disabled_booking_channels: [...] } }`. A save sends
 * only that key; merging it into the wider settings object is the backend's job.
 *
 * The fetch is skipped until the drawer opens and re-runs on each reopen, which is
 * how a saved value gets picked up again: this API slice declares no `tagTypes`,
 * so there is no invalidation to lean on.
 *
 * @param {number|string} integrationId `activeIntegrations[].id`, not the URL slug
 * @param {boolean} open Whether the drawer is open; gates the fetch
 */
export function useBookingChannels(integrationId, open) {
    const loading = useLoadingStates();
    const { showSuccessToast, showErrorToast } = useIntegrationToaster();

    const {
        data,
        isFetching,
        isError,
        error: fetchError,
    } = useGetBookingChannelsQuery(integrationId, {
        skip: !open || !integrationId,
        refetchOnMountOrArgChange: true,
    });

    const [updateBookingChannels] = useUpdateBookingChannelsMutation();

    const disabledValues = data?.settings?.disabled_booking_channels ?? NO_DISABLED_CHANNELS;

    const isSaving = loading.isLoading(BOOKING_CHANNELS_LOADING_KEYS.SAVE);

    // One toast per failed fetch. `showErrorToast` is a fresh reference every render,
    // so without the ref this would fire again on each re-render while isError holds.
    const hasNotifiedFetchError = useRef(false);

    useEffect(() => {
        if (!isError) {
            hasNotifiedFetchError.current = false;
            return;
        }

        if (hasNotifiedFetchError.current) return;

        hasNotifiedFetchError.current = true;
        showErrorToast(fetchError, toasterMessage.BOOKING_CHANNELS_FETCH_FAILED);
    }, [isError, fetchError, showErrorToast]);

    /**
     * @param {Array<string>} selectedValues Channels currently checked in the drawer
     * @returns {Promise<boolean>} true when saved, false when the request failed
     *
     * Resolving a boolean (rather than throwing) lets the drawer close on success
     * and stay open with every tick intact on failure. Passing `onError` to
     * `loading.execute` is also what guarantees the loading key is cleared, so the
     * Save button can never get stuck spinning.
     */
    // Not memoised on purpose: `useLoadingStates()` and `useIntegrationToaster()`
    // hand back fresh references every render, so a dependency array here would
    // either be dishonest or rebuild the callback anyway. `save` is only ever a
    // click handler, so a stable identity buys nothing.
    const save = async (selectedValues) => {
        const disabledChannels = getDisabledChannelValues(STR_BOOKING_CHANNELS, selectedValues);

        let saved = false;

        await loading.execute(
            BOOKING_CHANNELS_LOADING_KEYS.SAVE,
            () =>
                updateBookingChannels({
                    integrationId,
                    // Only the key this screen configures — the backend merges it into
                    // the rest of the settings object itself.
                    payload: { settings: { disabled_booking_channels: disabledChannels } },
                }).unwrap(),
            {
                onSuccess: () => {
                    saved = true;
                    showSuccessToast(toasterMessage.BOOKING_CHANNELS_SAVE_SUCCESS);
                },
                onError: (error) => {
                    showErrorToast(error, toasterMessage.BOOKING_CHANNELS_SAVE_FAILED);
                },
            }
        );

        return saved;
    };

    return {
        disabledValues,
        // `isFetching` rather than `isLoading`: a reopen refetches against a warm
        // cache, and the skeleton should cover that too instead of briefly showing
        // the previous integration's selection.
        isLoading: isFetching,
        isSaving,
        save,
    };
}

export default useBookingChannels;
